/**
 * Custom Buy Box — `CustomBuyBox` Alpine component
 * ------------------------------------------------------------------
 * A theme-native rebuild of the Recharge subscription buy box, now the DEFAULT
 * buy box on all subscription PDPs (bamboo + recycled toilet paper, kitchen
 * roll, tissues). The Recharge widget is superseded because it isn't flexible
 * enough. Config (variants, selling plans, prices) is generated per-product in
 * Liquid from `product.variants` + `variant.selling_plan_allocations`, so this
 * component is product-agnostic — it works for multi-variant products (toilet
 * paper: 24/48/96) and single-variant products (kitchen roll / tissues) alike.
 *
 * The trial-box feature has been removed (that test lost).
 *
 * Cart handoff: a direct POST to /cart/add.js. Recharge's
 * storefront-experiences.js monkey-patches window.fetch and auto-opens its own
 * shadow-DOM cart drawer when the add carries a selling_plan. The store's
 * cart_type is "page" (no theme drawer), so there is no double-drawer risk. We
 * deliberately do NOT fire the theme's shapes:cart:afteradditem event (see the
 * addToCart note) — mirroring how the Recharge control behaves.
 *
 * A/B test hooks (added to <html> in the IIFE below):
 *   .np-buybox-on  → show this buy box, hide the Recharge widget + native
 *                    variant picker + buy buttons. Added automatically on
 *                    product pages wherever `.np-buy-box` renders (i.e. the
 *                    custom box is the default), or forced via ?buybox=mirror.
 *   .np-sticky-on  → reveal the sticky Add-to-Cart bar. This is the tested
 *                    treatment: Varify adds it in the sticky arm; ?sticky=on
 *                    forces it for dev/QA.
 *
 * Loaded as a classic (non-module) deferred script; registers via the theme's
 * deferrable Alpine pattern (alpine:init, or immediately if Alpine has started).
 */
(function () {
  'use strict';

  try {
    var qs = new URLSearchParams(window.location.search);

    // Make the custom buy box the DEFAULT wherever it renders — the 6
    // subscription PDPs AND the homepage featured product (both go through the
    // same product-template → product-block-custom-buy-box path). Deferred script
    // → DOM is parsed, so `.np-buy-box` is present if rendered.
    var forceMirror = qs.get('buybox') === 'mirror';
    if (forceMirror || document.querySelector('.np-buy-box')) {
      document.documentElement.classList.add('np-buybox-on');
    }

    // Sticky Add-to-Cart test cohort.
    if (qs.get('sticky') === 'on') {
      document.documentElement.classList.add('np-sticky-on');
    }
  } catch (e) {
    /* no-op */
  }

  function defineCustomBuyBox() {
    window.Alpine.data('CustomBuyBox', function (config) {
      return {
        cfg: config,

        // ---- state ----
        mode: null, // the selected variant id (as a string key into cfg.modes)
        purchaseType: 'subscribe', // 'subscribe' | 'onetime'
        sellingPlanId: null,
        loading: false,
        cartAddError: '',
        addedToCart: false,
        modal: '', // open overlay: '' | 'subscribe'
        stickyShown: false, // sticky bar visibility (buy box scrolled out of view)
        cartOpen: false, // Recharge cart drawer/overlay open (hide sticky then)

        init: function () {
          // Default to the server-computed default (48 Rolls / Every 4 months on
          // the TP products — the winning A/B test, now native). Falls back to
          // the first variant / first plan when cfg carries no default (single-
          // variant products, or any product without a 48 variant).
          this.mode = this.cfg.defaultVariantId || this.cfg.modeOrder[0];
          this.purchaseType = 'subscribe';
          this.sellingPlanId =
            this.cfg.defaultSellingPlanId || this.firstPlanId(this.mode);

          // Honour ?variant=<id> deep-links (e.g. Google Shopping) — pre-select
          // that variant when it's one of ours.
          var params = new URLSearchParams(window.location.search);
          var variantParam = params.get('variant');
          if (variantParam && this.cfg.modes[variantParam]) {
            this.selectMode(variantParam);
          }

          this.initStickyObserver();
          this.initCartWatcher();
        },

        // Hide the sticky bar while the Recharge cart drawer/overlay is open (it
        // overlaps and is redundant then). Recharge's drawer is a React app using
        // `react-remove-scroll`, which sets `data-scroll-locked` on <body> (and a
        // `block-interactivity-*` class) whenever its overlay is open — we watch
        // for that. Works whether the drawer is opened by our add-to-cart or the
        // header cart icon; clears automatically when the drawer closes.
        initCartWatcher: function () {
          var self = this;
          var update = function () {
            self.cartOpen =
              document.body.hasAttribute('data-scroll-locked') ||
              /(^|\s)block-interactivity-/.test(document.body.className || '');
          };
          var mo = new MutationObserver(update);
          mo.observe(document.body, {
            attributes: true,
            attributeFilter: ['data-scroll-locked', 'class', 'style']
          });
          update();
        },

        // Reveal the sticky bar once the main buy box has scrolled out of view.
        initStickyObserver: function () {
          var self = this;
          var root = this.$root;
          if (!('IntersectionObserver' in window) || !root) return;
          var io = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                // Reveal ONLY once the buy box has scrolled ABOVE the viewport
                // (fully passed). On mobile the buy box starts below the fold,
                // which is also "not intersecting" — so we additionally require
                // its bottom edge to be at/above the viewport top.
                self.stickyShown =
                  !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
              });
            },
            { rootMargin: '0px', threshold: 0 }
          );
          io.observe(root);
        },

        // ---- derived ----
        get currentMode() {
          return this.cfg.modes[this.mode] || this.cfg.modes[this.cfg.modeOrder[0]];
        },
        get hasMultipleSizes() {
          return this.cfg.modeOrder.length > 1;
        },
        get isSubscribe() {
          return this.purchaseType === 'subscribe';
        },
        get currentVariantId() {
          return this.currentMode.variantId;
        },
        get currentPlans() {
          return this.currentMode ? this.currentMode.plans || [] : [];
        },
        get selectedPlan() {
          var plans = this.currentPlans;
          for (var i = 0; i < plans.length; i++) {
            if (plans[i].id === this.sellingPlanId) return plans[i];
          }
          return plans[0] || null;
        },
        get effectiveSellingPlanId() {
          return this.isSubscribe ? this.sellingPlanId : null;
        },
        get displayPriceCents() {
          if (this.isSubscribe && this.selectedPlan) return this.selectedPlan.subCents;
          return this.currentMode.oneTimeCents;
        },
        get comparePriceCents() {
          // Subscribe shows the one-time price struck through.
          return this.isSubscribe ? this.currentMode.oneTimeCents : null;
        },
        get currentPlanLabel() {
          return this.selectedPlan ? this.selectedPlan.label : '';
        },
        // Sticky bar frequency label: drop the "(3-4 people)" household
        // descriptor (present on toilet-paper plan names) for a compact line.
        get stickyFreqLabel() {
          if (!this.isSubscribe) return 'One-time';
          return (this.currentPlanLabel || '').replace(/\s*\(.*\)\s*$/, '');
        },
        get unitPriceLabel() {
          // Per-unit price = current price ÷ total sheets/tissues in the pack.
          // GBP's subunit is pence, so displayPriceCents is already pence —
          // divide straight through and trim to a tidy pence value (e.g.
          // "0.23p / sheet", "2.5p / tissue"). Updates live with size + plan.
          var m = this.currentMode;
          if (!m || !m.sheets) return '';
          var pence = this.displayPriceCents / m.sheets;
          var val = pence.toFixed(2).replace(/\.?0+$/, '');
          return val + 'p / ' + (this.cfg.unitLabel || 'sheet');
        },
        get addToCartLabel() {
          return 'Add to Cart';
        },

        // ---- helpers ----
        sizeLabel: function (key) {
          var m = this.cfg.modes[key];
          return m ? m.label : '';
        },
        firstPlanId: function (mode) {
          var m = this.cfg.modes[mode];
          return m && m.plans && m.plans.length ? m.plans[0].id : null;
        },
        money: function (cents) {
          if (typeof window.formatMoney === 'function') return window.formatMoney(cents);
          return '£' + (Number(cents) / 100).toFixed(2);
        },

        // ---- actions ----
        selectMode: function (mode) {
          this.mode = mode;
          this.cartAddError = '';
          this.addedToCart = false;
          if (this.purchaseType === 'subscribe') {
            this.sellingPlanId = this.firstPlanId(mode);
          }
        },
        setPurchaseType: function (type) {
          this.purchaseType = type;
          this.cartAddError = '';
          this.addedToCart = false;
          if (type === 'subscribe') {
            this.sellingPlanId = this.sellingPlanId || this.firstPlanId(this.mode);
          }
        },
        selectPlan: function (id) {
          this.sellingPlanId = id;
        },

        addToCart: function () {
          if (this.loading) return;
          this.loading = true;
          this.cartAddError = '';
          this.addedToCart = false;

          var item = { id: this.currentVariantId, quantity: 1 };
          var plan = this.effectiveSellingPlanId;
          if (plan) item.selling_plan = plan;

          var url =
            (window.theme && window.theme.routes && window.theme.routes.cart_add_url) ||
            '/cart/add.js';

          var self = this;
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [item] })
          })
            .then(function (res) {
              if (!res.ok) {
                return res.json().then(function (e) {
                  throw e;
                });
              }
              return res.json();
            })
            .then(function () {
              self.addedToCart = true;
              // We deliberately do NOT dispatch the theme's shapes:cart:afteradditem
              // event. On this Recharge store the cart is owned by Recharge: its
              // fetch patch (storefront-experiences.js) opens its own drawer for the
              // add, and native Recharge adds don't fire the theme cart event either.
            })
            .catch(function (err) {
              self.cartAddError =
                (err && err.description) ||
                'Sorry, something went wrong adding to your basket. Please try again.';
            })
            .finally(function () {
              self.loading = false;
            });
        }
      };
    });
  }

  if (window.alpineIsInitialized && window.Alpine) {
    defineCustomBuyBox();
  } else {
    document.addEventListener('alpine:init', defineCustomBuyBox);
  }
})();
